<template>
  <div class="modal fade" id="highlight-detail-dialog" tabindex="-1" aria-labelledby="highlightDetailLabel" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content detail-sheet">
        <DetailSheetHeader
          :title="title"
          :subtitle="subtitle"
          :icon-url="iconUrl"
          :icon-alt="prettyTypeName"
        />

        <!-- `p-3` (a !important utility) rather than a scoped rule, so the padding
             wins against Bootstrap's own .modal-body regardless of CSS order. -->
        <div class="modal-body p-3">
          <DetailSheetSection v-if="photos.length" plain>
            <DetailSheetPhotoStrip :photos="photos" @open="openLightbox" />
          </DetailSheetSection>

          <DetailSheetSection v-if="highlight?.description" plain>
            <p class="highlight-description">{{ displayedDescription }}</p>
            <button
              v-if="descriptionNeedsTruncation"
              type="button"
              class="btn btn-link btn-sm p-0 see-more-toggle"
              @click="descriptionExpanded = !descriptionExpanded"
            >
              {{ descriptionExpanded ? 'See less' : 'See more' }}
            </button>
          </DetailSheetSection>

          <DetailSheetSection v-if="audioTracks.length" plain>
            <div v-for="track in audioTracks" :key="track.key" class="audio-item">
              <p class="audio-item__title">
                <span class="material-icons">volume_up</span>{{ track.title || 'Audio' }}
              </p>
              <audio :src="track.url" controls preload="none" class="w-100"></audio>
            </div>
          </DetailSheetSection>

          <DetailSheetSection v-if="hasActions">
            <DetailSheetActionRow
              v-if="canEdit"
              label="Edit details"
              icon="edit"
              @click="editHighlight"
            />
            <DetailSheetActionRow
              v-if="canIncludeOnRoute"
              label="Include on route"
              icon="add_location_alt"
              @click="includeOnRoute"
            />
            <DetailSheetActionRow
              v-if="hasLocation"
              label="Plan a hike from here"
              icon="explore"
              @click="planHikeFromHighlight"
            />
            <DetailSheetActionRow
              v-if="hasLocation"
              label="Driving directions"
              icon="directions_car"
              @click="getDirections"
            />
            <DetailSheetActionRow
              v-if="hasLocation"
              label="View on Google Street View"
              icon="streetview"
              pro
              @click="openStreetView"
            />
            <DetailSheetActionRow
              v-if="shareUrl"
              label="Share highlight"
              icon="share"
              :hint="shareUrl"
              :status="showCopiedShare ? 'Copied!' : ''"
              @click="shareHighlight"
            />
            <DetailSheetActionRow
              v-if="canDelete"
              label="Delete highlight"
              icon="delete"
              danger
              @click="deleteHighlight"
            />
          </DetailSheetSection>

          <DetailSheetSection v-if="hasDetails">
            <DetailSheetMetaRow
              v-if="formattedCoordinates"
              label="Coords"
              :value="formattedCoordinates"
              copyable
              :copied="showCopiedCoords"
              @copy="copyCoordinates"
            />
            <DetailSheetMetaRow v-if="prettyTypeName" label="Type" :value="prettyTypeName" />
            <DetailSheetMetaRow v-if="highlight?.dayNumber" label="Day" :value="`Day ${highlight.dayNumber}`" />
            <DetailSheetMetaRow v-if="highlight?.id" label="ID" :value="String(highlight.id)" />
          </DetailSheetSection>
        </div>

        <div class="detail-sheet__footer">
          <button type="button" class="btn detail-sheet__close" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>

    <DetailSheetLightbox
      :open="lightboxOpen"
      :photos="photos"
      :index="lightboxIndex"
      @close="lightboxOpen = false"
      @prev="showPrevPhoto"
      @next="showNextPhoto"
    />
  </div>
</template>

<script lang="ts">
import { Modal } from 'bootstrap';
import { computed, defineComponent, onBeforeUnmount, ref } from 'vue';
import { useStore } from 'vuex';
import DetailSheetActionRow from '../../utils/shared_components/detail_sheet/DetailSheetActionRow.vue';
import DetailSheetHeader from '../../utils/shared_components/detail_sheet/DetailSheetHeader.vue';
import DetailSheetLightbox from '../../utils/shared_components/detail_sheet/DetailSheetLightbox.vue';
import DetailSheetMetaRow from '../../utils/shared_components/detail_sheet/DetailSheetMetaRow.vue';
import DetailSheetPhotoStrip from '../../utils/shared_components/detail_sheet/DetailSheetPhotoStrip.vue';
import DetailSheetSection from '../../utils/shared_components/detail_sheet/DetailSheetSection.vue';
import { detailSheetSubtitle } from '../../utils/helpers/detailSheetFormat';
import { GridRefUtils } from '../../utils/helpers/GridRefPreference';
import {
  highlightAudioTracks,
  highlightLatLng,
  highlightPhotos,
  highlightShareUrl,
} from '../../utils/helpers/highlightDetails';
import { googleStreetViewUrl, hikePlannerStartUrl, openGoogleMaps } from '../../utils/helpers/OpenOnGoogleMaps';
import { clampNextIndex, clampPrevIndex } from '../../utils/helpers/photoViewerNav';
import { POIUtility } from '../../utils/models/POI';
import { RevenueCatWrapper } from '../../utils/RevenueCatWrapper';
import { truncateAtWord, needsTruncation } from '../../utils/helpers/truncateText';

// The planner publishes these on `window` when it is the host app; they are
// read through a cast so this dialog carries no compile-time dependency on it.
const planner = () => window as any;

// Highlight descriptions can run to several paragraphs (e.g. the imported
// Causeway Coast Way guide text). Collapse anything longer than this behind a
// "See more" toggle so the dialog stays readable.
const DESCRIPTION_TRUNCATE_LIMIT = 300;

const COPIED_FEEDBACK_MS = 2000;

export default defineComponent({
  name: 'HighlightDetailDialog',
  components: {
    DetailSheetActionRow,
    DetailSheetHeader,
    DetailSheetLightbox,
    DetailSheetMetaRow,
    DetailSheetPhotoStrip,
    DetailSheetSection,
  },
  setup() {
    // Undefined when the host app mounts this dialog without a Vuex store; every
    // read below is optional so the component stays usable in that case.
    const store = useStore();
    const highlight = ref<any>(null);
    const descriptionExpanded = ref(false);
    const lightboxOpen = ref(false);
    const lightboxIndex = ref(0);
    const showCopiedShare = ref(false);
    const showCopiedCoords = ref(false);

    const title = computed(() => highlight.value?.title || 'Highlight');

    const prettyTypeName = computed(() => {
      if (!highlight.value?.type) return '';
      try {
        return POIUtility.fromString(highlight.value.type).prettyName();
      } catch (e) {
        return highlight.value.type;
      }
    });

    const iconUrl = computed(() => {
      if (!highlight.value?.type) return '';
      try {
        return POIUtility.fromString(highlight.value.type).getPublicImageIcon();
      } catch (e) {
        return POIUtility.fromString('other').getPublicImageIcon();
      }
    });

    const subtitle = computed(() => detailSheetSubtitle([
      prettyTypeName.value,
      highlight.value?.dayNumber ? `Day ${highlight.value.dayNumber}` : '',
    ]));

    const descriptionNeedsTruncation = computed(() => needsTruncation(
      highlight.value?.description,
      DESCRIPTION_TRUNCATE_LIMIT,
    ));

    const displayedDescription = computed(() => {
      const description = highlight.value?.description || '';
      return descriptionExpanded.value
        ? description
        : truncateAtWord(description, DESCRIPTION_TRUNCATE_LIMIT);
    });

    const photos = computed(() => highlightPhotos(highlight.value));
    const audioTracks = computed(() => highlightAudioTracks(highlight.value));
    // Retained alongside `audioTracks` because callers/specs read it directly.
    const hasAudio = computed(() => audioTracks.value.length > 0);

    const latLng = computed(() => highlightLatLng(highlight.value));
    const hasLocation = computed(() => latLng.value !== null);

    const formattedCoordinates = computed(() => {
      if (!latLng.value) return '';
      try {
        return String(GridRefUtils.preference().convert(latLng.value as any));
      } catch (e) {
        return `${latLng.value.lng}, ${latLng.value.lat}`;
      }
    });

    const shareUrl = computed(() => (typeof window === 'undefined'
      ? null
      : highlightShareUrl(highlight.value, window.location.href)));

    // The planner is the only host that can mutate a highlight or the route, so
    // the editing actions key off its store module being present.
    const inPlanner = computed(() => store?.state?.planTrail !== undefined);
    const canIncludeOnRoute = computed(() => inPlanner.value && hasLocation.value);
    const canDelete = computed(() => inPlanner.value && !!highlight.value?.clientId);
    // `window.openHighlightEditDialog` is not reactive, and the planner only
    // publishes it from its own onMounted, which runs after this child dialog has
    // already rendered once. A computed that read it directly cached `false` for
    // the life of the dialog and the Edit action never appeared, so the check is
    // re-taken each time a highlight is shown instead.
    const editorAvailable = ref(false);
    const canEdit = computed(() => inPlanner.value && editorAvailable.value);

    // Don't render a titled panel with nothing in it.
    const hasActions = computed(() => hasLocation.value || !!shareUrl.value || canEdit.value || canDelete.value);
    const hasDetails = computed(() => !!formattedCoordinates.value
      || !!prettyTypeName.value
      || !!highlight.value?.dayNumber
      || !!highlight.value?.id);

    const modalElement = () => document.getElementById('highlight-detail-dialog');

    const hideDialog = () => {
      const element = modalElement();
      if (!element) return null;
      const modal = Modal.getInstance(element);
      modal?.hide();
      return element;
    };

    // Bootstrap leaves a backdrop behind when a second modal is opened over one
    // that is already showing; trim any that no longer belong to a visible modal.
    const cleanupOrphanedBackdrops = () => {
      const openModals = document.querySelectorAll('.modal.show');
      const backdrops = document.querySelectorAll('.modal-backdrop');

      backdrops.forEach((backdrop, index) => {
        if (index >= openModals.length) backdrop.remove();
      });

      if (openModals.length === 0 && backdrops.length > 0) {
        document.body.classList.remove('modal-open');
        document.body.style.removeProperty('padding-right');
      }
    };

    const openLightbox = (index: number) => {
      lightboxIndex.value = index;
      lightboxOpen.value = true;
    };

    const showPrevPhoto = () => { lightboxIndex.value = clampPrevIndex(lightboxIndex.value); };
    const showNextPhoto = () => { lightboxIndex.value = clampNextIndex(lightboxIndex.value, photos.value.length); };

    const flashCopied = (flag: typeof showCopiedShare) => {
      flag.value = true;
      window.setTimeout(() => { flag.value = false; }, COPIED_FEEDBACK_MS);
    };

    const copyText = async (text: string, flag: typeof showCopiedShare) => {
      try {
        await navigator.clipboard.writeText(text);
        flashCopied(flag);
      } catch (err) {
        console.error('Unable to copy text to clipboard', err);
      }
    };

    const shareHighlight = () => {
      if (shareUrl.value) copyText(shareUrl.value, showCopiedShare);
    };

    const copyCoordinates = () => {
      if (formattedCoordinates.value) copyText(formattedCoordinates.value, showCopiedCoords);
    };

    const getDirections = () => {
      if (latLng.value) openGoogleMaps(latLng.value.lat, latLng.value.lng);
    };

    const planHikeFromHighlight = () => {
      if (!latLng.value) return;
      window.open(hikePlannerStartUrl(window.location.origin, latLng.value.lat, latLng.value.lng), '_blank');
    };

    const openStreetView = async () => {
      if (!latLng.value) return;
      const isPro = await RevenueCatWrapper.isProOrProPlus();
      if (!isPro) {
        window.location.href = '/purchases';
        return;
      }
      window.open(googleStreetViewUrl(latLng.value.lat, latLng.value.lng), '_blank');
    };

    const includeOnRoute = async () => {
      if (!latLng.value) return;

      if (typeof planner().canEditReference === 'function' && !planner().canEditReference()) {
        if (typeof planner().checkReferenceEditWarning !== 'function') return;
        const confirmed = await planner().checkReferenceEditWarning();
        if (!confirmed) return;
      }

      hideDialog();
      await store?.dispatch('planTrail/addMarkerAtCoordinates', {
        lng: latLng.value.lng,
        lat: latLng.value.lat,
      });
    };

    const editHighlight = () => {
      const highlightCopy = highlight.value ? JSON.parse(JSON.stringify(highlight.value)) : null;
      const element = modalElement();

      if (element && highlightCopy) {
        // Wait for the dismiss animation so the editor doesn't fight this
        // dialog's backdrop teardown.
        element.addEventListener('hidden.bs.modal', () => {
          planner().openHighlightEditDialog?.(highlightCopy);
        }, { once: true });
        hideDialog();
        return;
      }

      if (highlightCopy) planner().openHighlightEditDialog?.(highlightCopy);
    };

    const deleteHighlight = async () => {
      // eslint-disable-next-line no-alert
      if (!window.confirm('Are you sure you want to delete this highlight?')) return;

      const { clientId } = highlight.value;
      await store?.dispatch('planTrail/removeHighlightFromMap', clientId);
      store?.commit('planTrail/removeHikeHighlight', clientId);
      hideDialog();
    };

    const showHighlight = (highlightData: any) => {
      // The map click payload only carries the feature's GeoJSON properties, so
      // merge in the stored highlight (images, media) when we can find it.
      let resolved = highlightData;
      const stored = highlightData?.clientId
        ? store?.state?.planTrail?.hikeHighlights?.find((item: any) => item.clientId === highlightData.clientId)
        : null;

      if (stored) {
        resolved = { ...JSON.parse(JSON.stringify(stored)), ...highlightData };
      }

      cleanupOrphanedBackdrops();

      editorAvailable.value = typeof planner().openHighlightEditDialog === 'function';
      highlight.value = resolved;
      descriptionExpanded.value = false;
      lightboxOpen.value = false;
      lightboxIndex.value = 0;

      const element = modalElement();
      if (!element) return;

      const modal = Modal.getInstance(element) ?? new Modal(element);
      element.addEventListener('hidden.bs.modal', cleanupOrphanedBackdrops, { once: true });
      modal.show();
    };

    window.showHighlightDetail = showHighlight;

    onBeforeUnmount(() => {
      if (window.showHighlightDetail === showHighlight) delete window.showHighlightDetail;
      cleanupOrphanedBackdrops();
    });

    return {
      audioTracks,
      canDelete,
      canEdit,
      canIncludeOnRoute,
      copyCoordinates,
      deleteHighlight,
      descriptionExpanded,
      descriptionNeedsTruncation,
      displayedDescription,
      editHighlight,
      formattedCoordinates,
      getDirections,
      hasActions,
      hasAudio,
      hasDetails,
      hasLocation,
      highlight,
      iconUrl,
      includeOnRoute,
      lightboxIndex,
      lightboxOpen,
      openLightbox,
      openStreetView,
      photos,
      planHikeFromHighlight,
      prettyTypeName,
      shareHighlight,
      shareUrl,
      showNextPhoto,
      showPrevPhoto,
      showCopiedCoords,
      showCopiedShare,
      subtitle,
      title,
    };
  },
});
</script>

<style scoped>
.detail-sheet {
  /* Let the header's background follow the dialog's rounded corners. */
  overflow: hidden;
}

/* Preserve the paragraph breaks (\n) stored in the description instead of
   collapsing them the way default HTML white-space handling would. */
.highlight-description {
  margin: 0;
  white-space: pre-line;
  color: #fff;
  font-size: 0.9375rem;
  line-height: 1.55;
}

.see-more-toggle {
  color: var(--yellow);
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
}

.see-more-toggle:hover {
  color: var(--yellow);
  text-decoration: underline;
}

.audio-item + .audio-item {
  margin-top: 0.75rem;
}

.audio-item__title {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin: 0 0 0.25rem;
  color: var(--text-muted);
  font-size: 0.8125rem;
}

.audio-item__title .material-icons {
  font-size: 17px;
}

.detail-sheet__footer {
  padding: 0 1rem 1rem;
}

.detail-sheet__close {
  width: 100%;
  background: var(--gray);
  border: 1px solid var(--gray-border);
  color: var(--text-muted);
  font-weight: 600;
}

.detail-sheet__close:hover {
  background: var(--highlight);
  color: #fff;
}
</style>
